// The station's network printer address, set on the station (Labels → printer card). Kept
// on the device. In the Windows shell the main process keeps it too (its settings file): it
// sends print jobs and status checks to that address only (review CR3-12), so the page
// saves it there as well, and the shell's copy wins at start.

import { DEFAULT_PRINTER_PORT, type PrinterTarget } from '@enp-suite/bridge';
import { useSyncExternalStore } from 'react';

const KEY = 'station.printer';
const listeners = new Set<() => void>();
let cached: PrinterTarget | undefined | null = null; // null = not read yet

/** The Windows shell's settings (window.stationHost.settings), when there is one. */
interface ShellPrinterSettings {
  get(): Promise<{ printer?: PrinterTarget | null }>;
  set(patch: { printer: PrinterTarget | null }): Promise<{
    printer?: PrinterTarget | null;
    printerChange?: 'saved' | 'cancelled' | 'refused';
  } | void>;
}

/**
 * The Windows shell didn't take the printer (CR3-22): the person at the station said no in
 * the shell's own confirmation (`cancelled`), or it isn't a label printer's port on the local
 * network (`refused`). Nothing changed.
 */
export class PrinterChangeError extends Error {
  constructor(readonly reason: 'cancelled' | 'refused') {
    super(
      reason === 'cancelled'
        ? 'The printer change was cancelled'
        : 'That printer address is not allowed',
    );
    this.name = 'PrinterChangeError';
  }
}

function shellSettings(): ShellPrinterSettings | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window.stationHost as { settings?: ShellPrinterSettings } | undefined)?.settings;
}

function read(): PrinterTarget | undefined {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<PrinterTarget>;
    if (typeof parsed.host !== 'string' || !parsed.host) return undefined;
    const port = Number(parsed.port ?? DEFAULT_PRINTER_PORT);
    return { host: parsed.host, port: Number.isInteger(port) ? port : DEFAULT_PRINTER_PORT };
  } catch {
    return undefined;
  }
}

function keep(target: PrinterTarget | undefined) {
  try {
    if (target) localStorage.setItem(KEY, JSON.stringify(target));
    else localStorage.removeItem(KEY);
  } catch {
    // Storage unavailable (private mode): keep it for this session only.
  }
  cached = target;
  for (const l of listeners) l();
}

export function loadPrinterTarget(): PrinterTarget | undefined {
  if (cached === null) cached = read();
  return cached;
}

/**
 * At start, in the Windows shell: the shell's printer is the one that prints. A station set
 * up before the shell kept it hands its printer over once.
 */
export async function initPrinterTarget(): Promise<void> {
  const settings = shellSettings();
  if (!settings) return;
  try {
    const { printer } = await settings.get();
    if (printer) keep(printer);
    else {
      // The shell asks the person at the station to confirm the hand-over (CR3-22).
      const local = loadPrinterTarget();
      if (local) {
        const answer = await settings.set({ printer: local });
        if (answer && answer.printerChange && answer.printerChange !== 'saved') keep(undefined);
      }
    }
  } catch (err) {
    console.warn('The shell’s printer setting could not be read', err);
  }
}

/**
 * Saves the printer on the station (and in the Windows shell, which then prints to it). In
 * the shell the change counts only once the shell took it: it confirms it with the person at
 * the station and refuses addresses that aren't a label printer's (CR3-22), throwing
 * {@link PrinterChangeError}.
 */
export async function savePrinterTarget(target: PrinterTarget | undefined): Promise<void> {
  const settings = shellSettings();
  if (settings) {
    const answer = await settings.set({ printer: target ?? null });
    const change = answer ? answer.printerChange : undefined;
    if (change === 'cancelled' || change === 'refused') throw new PrinterChangeError(change);
  }
  keep(target);
}

export function usePrinterTarget(): PrinterTarget | undefined {
  return useSyncExternalStore((onChange) => {
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, loadPrinterTarget);
}

/** Validates what someone typed; returns an error message or the target. */
export function parsePrinterInput(
  host: string,
  port: string,
): { target: PrinterTarget } | { error: string } {
  const h = host.trim();
  if (!h) return { error: 'Enter the printer’s IP address or name.' };
  if (!/^[A-Za-z0-9.-]+$/.test(h)) return { error: 'Use an address like 192.168.1.50.' };
  const p = port.trim() === '' ? DEFAULT_PRINTER_PORT : Number(port);
  if (!Number.isInteger(p) || p < 1 || p > 65535)
    return { error: 'The port is a number from 1 to 65535 (usually 9100).' };
  return { target: { host: h, port: p } };
}
